import { ActivityEntityName } from '../shared/activity.entity.name';
import { EntityLink } from '../entity';
import { FinanceSecurityRight } from '../../security/modules/finance.security.module';
import { availableFinancialDocumentTransitions, PurchaseOrderLifecycleCommand, purchaseOrderLifecycle, resolveFinancialDocumentTransition } from '../financial/financial.document.lifecycle';
import { PurchaseOrder, PurchaseOrderCommandId, PurchaseOrderDeliverOptions, PurchaseOrderStatus, PurchaseOrderSupplier } from './purchase.order';
import { PurchaseOrderLine } from './purchase.order.line';

/** Create/update may accept only these top-level fields. Everything else is server-owned. */
export const PURCHASE_ORDER_WRITABLE_FIELDS = [
  'reference',
  'poDate',
  'deliveryDate',
  'deliveryOptions',
  'deliveryAddress',
  'deliveryInstructions',
  'supplier',
  'billingCompany',
  'comment',
  'theme',
  'priceBook',
  'attachments',
  'links',
  'job',
  'owner',
  'taxInclusiveOption',
  'taxRate',
  'lines',
  'kind',
  'changesRequiredNote',
] as const;

export type PurchaseOrderWritableField = (typeof PURCHASE_ORDER_WRITABLE_FIELDS)[number];

/** Fields clients must never mutate via generic create/update/PATCH. */
export const PURCHASE_ORDER_PROTECTED_FIELDS = [
  'status',
  'poNumber',
  'subTotal',
  'tax',
  'total',
  'createdAt',
  'updatedAt',
  'createdBy',
  'pdf',
  'accounting',
  'active',
  '__v',
] as const;

export type PurchaseOrderProtectedField = (typeof PURCHASE_ORDER_PROTECTED_FIELDS)[number];

/** Linked-item line fields that stay locked once `itemLink` is present. */
export const PURCHASE_ORDER_LINKED_LINE_LOCKED_FIELDS = [
  'name',
  'quantity',
  'uom',
  'codeUoM',
  'price',
  'itemLink',
  'catalogueCodeId',
  'type',
  'glCode',
] as const;

export type PurchaseOrderRights = {
  manage: boolean;
  process: boolean;
  approve: boolean;
};

export type PurchaseOrderMutationRejection = {
  field: string;
  reason: string;
};

export type PurchaseOrderCommandGate = {
  allowed: boolean;
  reason?: string;
  requiredRights?: ReadonlyArray<'manage' | 'process' | 'approve'>;
};

const COMMAND_TO_LIFECYCLE: Partial<Record<PurchaseOrderCommandId, PurchaseOrderLifecycleCommand>> = {
  [PurchaseOrderCommandId.Submit]: PurchaseOrderLifecycleCommand.SubmitForApproval,
  [PurchaseOrderCommandId.ChangesRequired]: PurchaseOrderLifecycleCommand.RequestChanges,
  [PurchaseOrderCommandId.Approve]: PurchaseOrderLifecycleCommand.Approve,
  [PurchaseOrderCommandId.MarkAsSent]: PurchaseOrderLifecycleCommand.MarkSent,
  [PurchaseOrderCommandId.SendPo]: PurchaseOrderLifecycleCommand.Send,
  [PurchaseOrderCommandId.Bill]: PurchaseOrderLifecycleCommand.GenerateBill,
  [PurchaseOrderCommandId.GenerateBill]: PurchaseOrderLifecycleCommand.GenerateBill,
  [PurchaseOrderCommandId.Cancel]: PurchaseOrderLifecycleCommand.Cancel,
  [PurchaseOrderCommandId.CancelAndCancelLinkedItems]: PurchaseOrderLifecycleCommand.Cancel,
};

/** Active lifecycle statuses only — deprecated model values are migration-only. */
export function purchaseOrderActiveStatuses(): readonly PurchaseOrderStatus[] {

  return purchaseOrderLifecycle.states.map(state => state.state);
}

export function isPurchaseOrderActiveStatus(status: PurchaseOrderStatus | string | undefined): boolean {

  return !!status && purchaseOrderActiveStatuses().includes(status as PurchaseOrderStatus);
}

export function isPurchaseOrderDeprecatedStatus(status: PurchaseOrderStatus | string | undefined): boolean {

  if (!status) return false;
  return Object.values(PurchaseOrderStatus).includes(status as PurchaseOrderStatus) && !isPurchaseOrderActiveStatus(status);
}

export function purchaseOrderEditability(status: PurchaseOrderStatus | undefined): 'editable' | 'conditional' | 'readonly' {

  const state = purchaseOrderLifecycle.states.find(entry => entry.state === status);
  return state?.editability ?? 'readonly';
}

export function isPurchaseOrderEditable(status: PurchaseOrderStatus | undefined, rights: PurchaseOrderRights): boolean {

  const editability = purchaseOrderEditability(status);
  if (editability === 'editable') return rights.manage;
  if (editability === 'conditional') return rights.manage && rights.approve;
  return false;
}

/**
 * Approved and Awaiting Delivery POs are otherwise locked, but still take the supplier's reference,
 * which usually arrives after approval. The entity-data strategy writes nothing but `reference` then.
 */
export function isPurchaseOrderReferenceOnlyEditable(status: PurchaseOrderStatus | undefined, rights: PurchaseOrderRights): boolean {

  return rights.manage && (status === PurchaseOrderStatus.Approved || status === PurchaseOrderStatus.AwaitingDelivery);
}

export function isPurchaseOrderTerminal(status: PurchaseOrderStatus | undefined): boolean {

  return purchaseOrderLifecycle.states.some(state => state.state === status && state.terminal);
}

/** Draft disposal is Delete — Cancel begins at Pending Approval. */
export function canDeletePurchaseOrder(status: PurchaseOrderStatus | undefined): boolean {

  return status === PurchaseOrderStatus.Draft;
}

/**
 * The named capability that opens removal of lines linked to job items: on Draft and Changes Required a user may
 * take an item off the PO, and the save unlinks the job item so it can go on another PO (WBS-13003, product decision
 * reversing the WBS-10506 lock for removal only). Later statuses keep linked lines. Read by the entity-data strategy
 * and by the legacy editor's line delete, so the screen offers exactly what the server accepts.
 */
export function canRemoveLinkedPurchaseOrderLines(status: PurchaseOrderStatus | undefined): boolean {

  return status === PurchaseOrderStatus.Draft || status === PurchaseOrderStatus.ChangesRequired;
}

export function canCancelPurchaseOrder(status: PurchaseOrderStatus | undefined): boolean {

  return !!resolveFinancialDocumentTransition(purchaseOrderLifecycle, status as PurchaseOrderStatus, PurchaseOrderLifecycleCommand.Cancel);
}

export function rightsFromFlags(flags: Partial<PurchaseOrderRights> | undefined): PurchaseOrderRights {

  return {
    manage: !!flags?.manage,
    process: !!flags?.process,
    approve: !!flags?.approve,
  };
}

export function rightsFromSecurityIds(rightIds: readonly number[] | undefined): PurchaseOrderRights {

  const set = new Set(rightIds ?? []);
  return {
    manage: set.has(FinanceSecurityRight.ManagePurchaseOrder),
    process: set.has(FinanceSecurityRight.ProcessingPurchaseOrder),
    approve: set.has(FinanceSecurityRight.ApprovePurchaseOrder),
  };
}

/**
 * Contract rights matrix:
 * - Create/edit Draft or Changes Required → Manage
 * - Submit, dispatch, cancel → Process
 * - Approve / request changes → Approve
 * - Generate Bill → Manage + Process
 * - Edit Pending Approval → Manage + Approve
 */
export function authorizePurchaseOrderCommand(commandId: PurchaseOrderCommandId, rights: PurchaseOrderRights): PurchaseOrderCommandGate {

  switch (commandId) {
    case PurchaseOrderCommandId.Submit:
    case PurchaseOrderCommandId.MarkAsSent:
    case PurchaseOrderCommandId.SendPo:
    case PurchaseOrderCommandId.Cancel:
    case PurchaseOrderCommandId.CancelAndCancelLinkedItems:
      return rights.process
        ? { allowed: true }
        : { allowed: false, reason: 'Process Purchase Order right is required.', requiredRights: ['process'] };

    case PurchaseOrderCommandId.Approve:
    case PurchaseOrderCommandId.ChangesRequired:
      return rights.approve
        ? { allowed: true }
        : { allowed: false, reason: 'Approve Purchase Order right is required.', requiredRights: ['approve'] };

    case PurchaseOrderCommandId.Bill:
    case PurchaseOrderCommandId.GenerateBill:
      return rights.manage && rights.process
        ? { allowed: true }
        : { allowed: false, reason: 'Manage and Process Purchase Order rights are required to generate a bill.', requiredRights: ['manage', 'process'] };

    case PurchaseOrderCommandId.NewTemplate:
    case PurchaseOrderCommandId.GetAttachment:
      return rights.manage || rights.process || rights.approve
        ? { allowed: true }
        : { allowed: false, reason: 'A Purchase Order right is required.', requiredRights: ['manage'] };

    default:
      return { allowed: false, reason: `Unknown purchase order command ${commandId}.` };
  }
}

export function authorizePurchaseOrderMutation(status: PurchaseOrderStatus | undefined, rights: PurchaseOrderRights): PurchaseOrderCommandGate {

  if (!rights.manage) {
    return { allowed: false, reason: 'Manage Purchase Order right is required.', requiredRights: ['manage'] };
  }
  if (status === PurchaseOrderStatus.PendingApproval && !rights.approve) {
    return { allowed: false, reason: 'Editing a pending approval purchase order requires Manage and Approve rights.', requiredRights: ['manage', 'approve'] };
  }
  if (!isPurchaseOrderEditable(status, rights)) {
    return { allowed: false, reason: `Purchase order in status ${status ?? 'unknown'} is not editable.` };
  }
  return { allowed: true };
}

export function resolvePurchaseOrderLifecycleCommand(commandId: PurchaseOrderCommandId): PurchaseOrderLifecycleCommand | undefined {

  return COMMAND_TO_LIFECYCLE[commandId];
}

export function authorizePurchaseOrderTransition(status: PurchaseOrderStatus | undefined, commandId: PurchaseOrderCommandId): PurchaseOrderCommandGate {

  const lifecycleCommand = resolvePurchaseOrderLifecycleCommand(commandId);
  if (!lifecycleCommand) {
    return { allowed: true };
  }
  if (!status || !isPurchaseOrderActiveStatus(status)) {
    return { allowed: false, reason: `Purchase order status ${status ?? 'unknown'} cannot accept lifecycle commands.` };
  }
  const transition = resolveFinancialDocumentTransition(purchaseOrderLifecycle, status, lifecycleCommand);
  if (!transition) {
    const allowed = availableFinancialDocumentTransitions(purchaseOrderLifecycle, status).map(entry => entry.command);
    return { allowed: false, reason: `Command ${commandId} is not allowed from ${status}. Allowed: ${allowed.join(', ') || 'none'}.` };
  }
  return { allowed: true };
}

export function pickPurchaseOrderWritableFields(input: Partial<PurchaseOrder> | Record<string, unknown> | null | undefined): Partial<PurchaseOrder> {

  const source = (input ?? {}) as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const field of PURCHASE_ORDER_WRITABLE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      result[field] = source[field];
    }
  }
  if (Object.prototype.hasOwnProperty.call(source, 'lines')) {
    result['lines'] = sanitizePurchaseOrderLinesForWrite(source['lines'] as PurchaseOrderLine[] | undefined, undefined);
  }
  return result as Partial<PurchaseOrder>;
}

export function findProtectedPurchaseOrderFields(input: Partial<PurchaseOrder> | Record<string, unknown> | null | undefined): PurchaseOrderMutationRejection[] {

  const source = (input ?? {}) as Record<string, unknown>;
  const rejections: PurchaseOrderMutationRejection[] = [];
  for (const field of PURCHASE_ORDER_PROTECTED_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field) && source[field] !== undefined) {
      rejections.push({ field, reason: `${field} is server-owned and cannot be set by create/update.` });
    }
  }
  if (Object.prototype.hasOwnProperty.call(source, '_id') && source['_id'] != null && source['_id'] !== '') {
    // Identity may be present for updates; create must not forge ids through allowlist alone.
  }
  return rejections;
}

export function assertNoProtectedPurchaseOrderFields(input: Partial<PurchaseOrder> | Record<string, unknown> | null | undefined): void {

  const rejections = findProtectedPurchaseOrderFields(input);
  if (rejections.length) {
    throw new Error(rejections.map(entry => entry.reason).join(' '));
  }
}

/**
 * Bill lineage is stamped by Generate Bill and is the only link a client may not author.
 * Job / Work source lineage is set when the PO is built from that record and must survive create.
 */
const SERVER_OWNED_PURCHASE_ORDER_LINK_TYPES = new Set<string>([
  ActivityEntityName.Bill,
]);

export function isServerOwnedPurchaseOrderLink(link: EntityLink | null | undefined): boolean {

  return !!link?.type && SERVER_OWNED_PURCHASE_ORDER_LINK_TYPES.has(link.type);
}

/** Job/Work source links are accepted on create/update; Bill lineage is preserved from the stored record. */
export function sanitizePurchaseOrderClientLinks(links: EntityLink[] | undefined, existing?: EntityLink[]): EntityLink[] {

  const preserved = (existing ?? []).filter(isServerOwnedPurchaseOrderLink);
  const clientLinks = (links ?? []).filter(link => !isServerOwnedPurchaseOrderLink(link));
  return [...clientLinks, ...preserved];
}

/**
 * Source lineage is authored once, when the PO is built from its Job or Work record.
 * Create accepts the client's source links because only the client knows which record the
 * PO came from. Update returns `undefined` — meaning "leave links alone" — so a client can
 * neither re-point lineage at an unrelated record nor clobber a concurrently stamped Bill link.
 * No editor surface exposes links for editing, so nothing legitimate needs to change them.
 */
export function resolvePurchaseOrderWriteLinks(clientLinks: EntityLink[] | undefined, isCreate: boolean): EntityLink[] | undefined {

  if (!isCreate) return undefined;
  return sanitizePurchaseOrderClientLinks(clientLinks, undefined);
}

/** A Job link must name the same Job as the `job` field it ships with — the builders always agree. */
export function purchaseOrderSourceLinkViolations(links: EntityLink[] | undefined, job: PurchaseOrder['job'] | null | undefined): PurchaseOrderMutationRejection[] {

  if (!job?._id) return [];
  const mismatched = (links ?? []).filter(link => link?.type === ActivityEntityName.Job && link._id && link._id !== job._id);
  if (!mismatched.length) return [];
  return [{ field: 'links', reason: `Job link ${mismatched[0]._id} does not match the purchase order job ${job._id}.` }];
}

/** Linked lines keep provenance and commercial facts unless a named domain capability opens them. */
export function isPurchaseOrderLineLinked(line: PurchaseOrderLine | null | undefined): boolean {

  return !!(line?.itemLink?.lineId || line?.itemLink?._id);
}

/**
 * Locks the commercial fields of linked lines to their stored values. A linked line missing from the write is put
 * back unless `allowLinkedLineRemoval` (from `canRemoveLinkedPurchaseOrderLines`) lets it go.
 */
export function sanitizePurchaseOrderLinesForWrite(nextLines: PurchaseOrderLine[] | undefined, previousLines: PurchaseOrderLine[] | undefined, allowLinkedLineRemoval = false): PurchaseOrderLine[] {

  const previousById = new Map((previousLines ?? []).filter(line => line?._id).map(line => [line._id as string, line]));
  const previousLinkedIds = new Set((previousLines ?? []).filter(isPurchaseOrderLineLinked).map(line => line._id).filter((id): id is string => !!id));
  const next = (nextLines ?? []).map(line => {
    if (!line?._id) return { ...line };
    const previous = previousById.get(line._id);
    if (!previous || !isPurchaseOrderLineLinked(previous)) return { ...line };
    return lockLinkedPurchaseOrderLine(line, previous);
  });

  if (allowLinkedLineRemoval) return next;

  // Outside the removal capability a generic write cannot drop linked rows.
  for (const linkedId of previousLinkedIds) {
    if (!next.some(line => line._id === linkedId)) {
      const previous = previousById.get(linkedId);
      if (previous) next.push({ ...previous });
    }
  }

  return next;
}

export function lockLinkedPurchaseOrderLine(next: PurchaseOrderLine, previous: PurchaseOrderLine): PurchaseOrderLine {

  const locked = { ...next };
  for (const field of PURCHASE_ORDER_LINKED_LINE_LOCKED_FIELDS) {
    (locked as unknown as Record<string, unknown>)[field] = (previous as unknown as Record<string, unknown>)[field];
  }
  return locked;
}

/** Rejects edits to a linked line's locked fields, and its removal unless `allowLinkedLineRemoval` permits it. */
export function linkedPurchaseOrderLineViolations(nextLines: PurchaseOrderLine[] | undefined, previousLines: PurchaseOrderLine[] | undefined, allowLinkedLineRemoval = false): PurchaseOrderMutationRejection[] {

  const previousById = new Map((previousLines ?? []).filter(line => line?._id).map(line => [line._id as string, line]));
  const rejections: PurchaseOrderMutationRejection[] = [];
  for (const previous of previousLines ?? []) {
    if (!isPurchaseOrderLineLinked(previous) || !previous._id) continue;
    const next = (nextLines ?? []).find(line => line._id === previous._id);
    if (!next) {
      if (!allowLinkedLineRemoval) {
        rejections.push({ field: `lines.${previous._id}`, reason: 'Linked purchase order lines cannot be removed.' });
      }
      continue;
    }
    for (const field of PURCHASE_ORDER_LINKED_LINE_LOCKED_FIELDS) {
      if (!sameJson((next as unknown as Record<string, unknown>)[field], (previous as unknown as Record<string, unknown>)[field])) {
        rejections.push({ field: `lines.${previous._id}.${field}`, reason: `Linked line field ${field} is locked.` });
      }
    }
  }
  return rejections;
}

export function validatePurchaseOrderSupplier(supplier: PurchaseOrderSupplier | null | undefined): PurchaseOrderMutationRejection[] {

  if (!supplier?._id) {
    return [{ field: 'supplier', reason: 'Supplier is required.' }];
  }
  if (!supplier.name) {
    return [{ field: 'supplier.name', reason: 'Supplier name is required.' }];
  }
  return [];
}

/** Supplier parent ID scopes supplier boards — never Job relation scope. */
export function resolvePurchaseOrderJobScope(po: Pick<PurchaseOrder, 'job' | 'links' | 'supplier'> | null | undefined): string | undefined {

  if (po?.job?._id) return po.job._id;
  const jobLink = (po?.links ?? []).find(link => link?.type === ActivityEntityName.Job && link._id);
  return jobLink?._id;
}

export function isSupplierParentScope(parentId: string | undefined, po: Pick<PurchaseOrder, 'supplier' | 'job' | 'links'> | null | undefined): boolean {

  if (!parentId || !po?.supplier?._id) return false;
  return parentId === po.supplier._id && parentId !== resolvePurchaseOrderJobScope(po);
}

export function validatePurchaseOrderDelivery(options: PurchaseOrderDeliverOptions | undefined, deliveryAddress: PurchaseOrder['deliveryAddress'] | undefined): PurchaseOrderMutationRejection[] {

  const mode = options ?? 'pickup';
  if (mode === 'pickup') return [];
  if (mode === 'company' || mode === 'job' || mode === 'other') {
    if (!deliveryAddress?.address) {
      return [{ field: 'deliveryAddress', reason: `Delivery option ${mode} requires a resolved delivery address.` }];
    }
  }
  return [];
}

export function purchaseOrderStageWorkIdempotencyKey(poId: string, lineId: string): string {

  return `po-stage:${poId}:${lineId}`;
}

/**
 * Gives every PO line its own `_id` before approval builds Stage work from it; ids that are already unique are kept.
 *
 * The Stage item id is derived from the line id (purchaseOrderStageWorkIdempotencyKey). Field App POs arrive with no
 * line ids, so every item on such a PO shared `po-stage:<po>:undefined` and editing one overwrote the others (WBS-13189).
 * A missing or repeated id is replaced by one built from the PO and the line's position, not a random one, so a retried
 * approval assigns the same ids and its Stage work stays idempotent.
 */
export function withUniquePurchaseOrderLineIds(poId: string, lines: PurchaseOrderLine[] | null | undefined): PurchaseOrderLine[] {

  const seen = new Set<string>();

  return (lines ?? []).map((line, index) => {
    const keepsOwnId = !!line?._id && !seen.has(line._id);
    const lineId = keepsOwnId ? line._id : `po-line:${poId}:${index}`;

    seen.add(lineId);
    return keepsOwnId ? line : { ...line, _id: lineId };
  });
}

/** Approval creates Stage work once for Job-kind POs that have no existing itemLink. */
export function shouldCreatePurchaseOrderStageWorkOnApprove(po: Pick<PurchaseOrder, 'kind' | 'job' | 'links' | 'lines'> | null | undefined): boolean {

  if (!po) return false;
  const isJobLink = (po.links ?? []).some(link => link?.type === ActivityEntityName.Job) || !!po.job?._id;
  const isJobKind = po.kind === ActivityEntityName.Job;
  if (!isJobLink || !isJobKind) return false;
  if ((po.lines ?? []).some(line => !!line?.itemLink?.lineId)) return false;
  return true;
}

export function shouldDelegatePurchaseOrderCancelToStageItems(cancelLinkedItems: boolean, po: Pick<PurchaseOrder, 'links' | 'lines'> | null | undefined): boolean {

  if (!cancelLinkedItems || !po) return false;
  return !!(po.links ?? []).some(link => link?.type === ActivityEntityName.Job && link._id);
}

export function purchaseOrderLinkedItemsBlockCancel(lines: PurchaseOrderLine[] | undefined, billedOrInvoiced: (stageItemId: string) => boolean): string | undefined {

  for (const line of lines ?? []) {
    const stageItemId = line?.itemLink?.lineId;
    if (!stageItemId) continue;
    if (billedOrInvoiced(stageItemId)) {
      return `There are items already billed or invoiced. Cannot cancel linked items to the purchase order.`;
    }
  }
  return undefined;
}

export function purchaseOrderDeliveryChoices(hasJob: boolean): readonly { value: PurchaseOrderDeliverOptions; label: string }[] {

  return [
    { value: 'pickup', label: 'Pickup' },
    { value: 'company', label: 'Company' },
    ...(hasJob ? [{ value: 'job' as const, label: 'Job' }] : []),
    { value: 'other', label: 'Other' },
  ];
}

/** Resolves deliveryAddress.address from the selected option — mirrors legacy editor. */
export function resolvePurchaseOrderDeliveryAddress(args: {
  option: PurchaseOrderDeliverOptions | undefined;
  supplier?: { site?: { address?: unknown }; address?: unknown } | null;
  billingCompany?: { address?: unknown } | null;
  jobAddress?: unknown;
}): { address: unknown; cleared: boolean } {

  switch (args.option ?? 'pickup') {
    case 'pickup':
      return { address: args.supplier?.site?.address ?? args.supplier?.address ?? null, cleared: false };
    case 'company':
      return { address: args.billingCompany?.address ?? null, cleared: false };
    case 'job':
      return { address: args.jobAddress ?? null, cleared: false };
    case 'other':
      return { address: null, cleared: true };
    default:
      return { address: null, cleared: false };
  }
}

export function isJobStatusBlockingPurchaseOrderMutation(jobStatus: string | undefined): boolean {

  return jobStatus === 'Closed' || jobStatus === 'Finalised';
}

export function mergeMountedPurchaseOrderIdentity(submitted: Record<string, unknown>, mounted: Record<string, unknown> | null | undefined): Record<string, unknown> {

  const source = mounted ?? {};
  const result = { ...submitted };
  if (source['_id'] != null && result['_id'] == null) result['_id'] = source['_id'];
  // Canonical supplier projection wins over virtual account/site/contact binds.
  if (source['supplier'] != null && result['supplier'] == null) result['supplier'] = source['supplier'];
  if (source['billingCompany'] != null && result['billingCompany'] == null) result['billingCompany'] = source['billingCompany'];
  if (source['links'] != null && result['links'] == null) result['links'] = source['links'];
  if (source['status'] != null && result['status'] == null) result['status'] = source['status'];
  if (source['poNumber'] != null && result['poNumber'] == null) result['poNumber'] = source['poNumber'];
  return result;
}

export function stripPurchaseOrderVirtualBinds(model: Record<string, unknown>): Record<string, unknown> {

  const {
    purchaseOrderSupplierAccount: _account,
    purchaseOrderSupplierSite: _site,
    purchaseOrderSupplierContact: _contact,
    $variables: _variables,
    ...rest
  } = model;
  return rest;
}

function sameJson(left: unknown, right: unknown): boolean {

  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}
